import axios, { AxiosInstance } from 'axios';

export enum Features {
  HOMEPAGE_SURVEY = 'show_homepage_survey',
  MAINTENANCE_MODE = 'maintenance_mode',
  LANDING_PAGE_BANNER = 'show_landing_page_banner',
  BANNER_MARKDOWN = 'survey_banner_markdown'
}

export type FeatureFlags = {
  [key in Features]?: boolean | string;
};

/**
 * On page load we start the feature flag service
 * Services gets flags every 30 seconds
 * Hook with list of flags made available to components
 */
export abstract class FeatureFlagService {
  private static _client: AxiosInstance;
  private static skipNext: number;
  private static lastExecution: number;
  private static flags: FeatureFlags;
  private static intervalId?: NodeJS.Timeout;

  private static callback: (flags: FeatureFlags) => any;

  private static options: {
    intervalTime: number;
    initialFlags?: FeatureFlags;
  } = { intervalTime: 30000 };

  /** Private methods */
  private static get client() {
    if (this._client) return this._client;
    const client = axios.create({
      baseURL: process.env.REACT_APP_LAMBDAS_URL
    });
    this._client = client;
    return client;
  }

  static isRunning() {
    return !!this.intervalId;
  }

  /**
   * Fetch flags from cache frist and then from API
   */
  private static async fetchFlags() {
    const cachedFlags = this.getFlagsFromCache();
    if (cachedFlags) return cachedFlags;
    const res = await this.client.get<FeatureFlags>('flags/web');
    this.cacheFlags(res.data);
    return res.data;
  }

  /**
   * Retrieve flags from local storage if they exist and are not stale
   */
  private static getFlagsFromCache() {
    const cache = localStorage.getItem('flags');
    if (!cache) return;
    const { flags, timestamp } = JSON.parse(cache);
    if (Date.now() - timestamp > this.options.intervalTime * 0.95) return;
    return flags as FeatureFlags;
  }

  /**
   * Save flags to local storage with a timestamp for other tabs to access
   */
  private static cacheFlags(flags: FeatureFlags) {
    localStorage.setItem('flags', JSON.stringify({ flags, timestamp: Date.now() }));
  }

  /**
   * DO NOT CALL ELSEWHERE. Helper method for start function
   *
   * Fetches flags from API and compares to current flags
   * If flags have changed, calls callback with new flags
   */
  private static async loopFn(onError?: (error: any) => void) {
    try {
      if (this.skipNext && this.skipNext > 0) {
        this.skipNext--;
        return;
      }

      // Don't fetch if last fetch was less than 90% of interval time ago to prevent interval stacking
      if (this.lastExecution && Date.now() - this.lastExecution < this.options.intervalTime * 0.9)
        return;

      const flags = await this.fetchFlags().then(this.modifyFlags);

      // Only update flags if they have changed
      if (this.flags && this.compareFlags(flags, this.flags)) return;

      this.flags = flags;

      this.callback(flags);
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        throw error;
      }
    }
  }

  /**
   * Modify flags depending on context
   *
   * @privateRemarks
   * Forces Features.AVAILABLE to true if running on backdoor url
   */
  private static modifyFlags(flags: FeatureFlags) {
    const isBackdoor = /[\s\S]+firebaseapp\.com$/.test(window.location.origin);
    if (!isBackdoor) return flags;
    return { ...flags, [Features.MAINTENANCE_MODE]: false };
  }

  /** Public methods */

  /**
   * Start the feature flag service if one is not already running
   * @param callback - function to call when flags are updated
   * @param options.interval - interval in milliseconds to fetch flags
   * @param options.initialFlags - initial flags to use while first fetch is pending
   * @returns default flags
   */
  static start(
    callback: (flags: FeatureFlags) => any,
    options?: { interval?: number; initialFlags?: FeatureFlags; onError?: (error: any) => void }
  ) {
    // If service is already running, do nothing
    if (this.isRunning()) return;

    // Set configuration params
    this.callback = callback;
    if (options?.interval) this.options.intervalTime = options.interval;
    if (options?.initialFlags) {
      this.options.initialFlags = options.initialFlags;
      this.flags = options.initialFlags;
    }

    // Initialize fetch loop
    // (loopFn is wrapped in an arrow function to preserve this context)
    this.intervalId = setInterval(() => this.loopFn(options?.onError), this.options.intervalTime);
    this.loopFn(options?.onError);

    return this.flags;
  }

  /**
   * Compare two feature flag objects
   * @param a - first feature flag object
   * @param b - second feature flag object
   * @returns true if objects are the same, false otherwise
   */
  static compareFlags(a: FeatureFlags, b: FeatureFlags) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every((key) => a[key] === b[key]);
  }

  static async stop() {
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = undefined;
  }

  static async skip(number = 1) {
    this.skipNext = number;
  }
}
